import { EPostStatus } from '@constants';
import { ICreatePost, IPost } from '@sdk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

import { sdk } from '@/services';

const PostService = sdk.getService('PostService');

export const useMutatePost = () => {
  const { t } = useTranslation('CommonDashboardTranslations');
  const queryClient = useQueryClient();

  const onSuccess = () => queryClient.invalidateQueries({ queryKey: ['posts'] });

  const { isPending: isCreating, mutate: createPost } = useMutation({
    mutationFn: (data: ICreatePost) => PostService.createPost(data),
    onSuccess: res => {
      onSuccess();
      showSuccess(res.status);
    }
  });

  const { mutate: updatePost, isPending: isUpdating } = useMutation({
    mutationFn: (data: Partial<IPost>) => PostService.updatePost(data),
    onSuccess: () => {
      onSuccess();
      message.success(t('POST_UPDATED_SUCCESSFULLY'));
    }
  });

  const { mutate: deletePost, isPending: isDeleting } = useMutation({
    mutationFn: (postId: string) => PostService.remove(postId),
    onSuccess: () => {
      onSuccess();
      message.success(t('POST_DELETED_SUCCESSFULLY'));
    }
  });

  const { mutate: publishNow, isPending: isPublishing } = useMutation({
    mutationFn: (postId: string) => PostService.publishNow(postId),
    onSuccess: () => {
      onSuccess();
      message.success(t('POST_PUBLISHED_NOW_SUCCESSFULLY'));
    }
  });

  const showSuccess = (status: EPostStatus) => {
    switch (status) {
      case EPostStatus.PUBLISHED:
        message.success(t('NEWS_PUBLISHED_SUCCESS'));
        break;
      case EPostStatus.SCHEDULED:
        message.success(t('NEWS_SCHEDULED_SUCCESS'));
        break;
      default:
        message.success(t('NEWS_DRAFT_SUCCESS'));
    }
  };

  return {
    isPending: isCreating || isUpdating || isDeleting || isPublishing,
    createPost,
    updatePost,
    deletePost,
    publishNow
  };
};
